import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getMenu, createOrder, getOrders, lookupCustomer, getStore } from '@/services/api';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import PostPayEditModal from './PostPayEditModal';
import ProductCustomizer from '@/components/catalog/ProductCustomizer';
import { 
  Search, X, Plus, Minus, LogOut, Menu, Store, Eye, Clock, 
  Pause, Bell, Phone, HelpCircle, User, Users, Utensils, 
  Printer, Trash2, ChevronDown, ChevronUp, RefreshCw,
  MapPin, CheckCircle, XCircle, Bike, UserPlus, Percent, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import wsService from '@/services/websocket';
import { formatCurrency } from '@/utils/currency';
import PaymentDialog from '@/components/POS/PaymentDialog';

const POSAppNew = () => {
  const [user, setUser] = useState(null);
  const [menu, setMenu] = useState([]);
  const [customizingItem, setCustomizingItem] = useState(null);
  const [cart, setCart] = useState([]);
  const [mobileView, setMobileView] = useState('menu'); // 'menu' | 'cart' — phone screens only, matching Assisted Order
  const [activeOrders, setActiveOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [holdOrders, setHoldOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  
  // UI State
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [orderType, setOrderType] = useState('DINE_IN');
  
  // Customer & Order State
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', address: '', table: '' });
  const [isPostPay, setIsPostPay] = useState(false);
  const [collectPaymentOrder, setCollectPaymentOrder] = useState(null);
  const [postPayEditOrder, setPostPayEditOrder] = useState(null);
  const [showPostPayEditModal, setShowPostPayEditModal] = useState(false);
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const [customerLookupStatus, setCustomerLookupStatus] = useState(null); // null | 'found' | 'new'
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isComplimentary, setIsComplimentary] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [isLoyalty, setIsLoyalty] = useState(false);
  
  // COMPREHENSIVE DISCOUNT SYSTEM - Apply Only (No Configuration at Billing)
  // selectedDiscount: The dashboard-configured discount selected by operator
  const [selectedDiscount, setSelectedDiscount] = useState(null); 
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  const [showDiscountSelector, setShowDiscountSelector] = useState(false);
  
  // Legacy states - kept for backward compatibility
  const [categoryDiscounts, setCategoryDiscounts] = useState({});
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  
  // Combo Offers State
  const [comboOffers, setComboOffers] = useState([]);
  const [appliedCombo, setAppliedCombo] = useState(null);
  const [showComboSelector, setShowComboSelector] = useState(false);
  
  // Modals
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showLiveViewModal, setShowLiveViewModal] = useState(false);
  const [onlineOnlyFilter, setOnlineOnlyFilter] = useState(false);
  const [showRecentModal, setShowRecentModal] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [showAssignRiderModal, setShowAssignRiderModal] = useState(false);
  const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [selectedOrderForRider, setSelectedOrderForRider] = useState(null);
  const [selectedOrderForEdit, setSelectedOrderForEdit] = useState(null);
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState(null);
  const [editCustomerInfo, setEditCustomerInfo] = useState({ name: '', phone: '', address: '' });
  const [selectedRider, setSelectedRider] = useState('');
  
  // Store config
  const [storeConfig, setStoreConfig] = useState({ 
    tax_settings: { 
      enabled: true, 
      percent: 5,      // Total tax 5%
      cgst: 2.5,       // CGST 2.5%
      sgst: 2.5,       // SGST 2.5%
      show_breakdown: true
    },
    service_charge: {
      enabled: true,
      percent: 5       // Service charge 5%
    },
    show_images_in_pos: true
  });
  
  const [isCartExpanded, setIsCartExpanded] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Restore an in-progress order (cart, customer, order type, etc.) if the
  // page was refreshed mid-order. Only restores if there's actually
  // something in the saved cart, so a stale empty entry never overwrites
  // fresh state on a normal first load.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.cart && parsed.cart.length > 0) {
          setCart(parsed.cart);
          setCustomerInfo(parsed.customerInfo || { name: '', phone: '', address: '', table: '' });
          setOrderType(parsed.orderType || 'DINE_IN');
          setIsPostPay(parsed.isPostPay || false);
          setPaymentMethod(parsed.paymentMethod || 'CASH');
          setSpecialInstructions(parsed.specialInstructions || '');
          toast.success('Restored your in-progress order');
        }
      }
    } catch (e) {
      console.error('Failed to restore in-progress order:', e);
    }
  }, []);

  // Keep the in-progress order saved as it changes, so a refresh never
  // silently wipes an order staff are mid-way through building.
  useEffect(() => {
    if (cart.length === 0) {
      sessionStorage.removeItem(CART_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify({
      cart, customerInfo, orderType, isPostPay, paymentMethod, specialInstructions
    }));
  }, [cart, customerInfo, orderType, isPostPay, paymentMethod, specialInstructions]);

  // If we arrived here via the "Online Orders" dashboard button, auto-open
  // the Live Order View pre-filtered to online-only orders.
  useEffect(() => {
    if (location.state?.openLiveView === 'online') {
      setOnlineOnlyFilter(true);
      setShowLiveViewModal(true);
      // clear the state so a later refresh/back doesn't re-trigger this
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Get unique categories from menu
  const categories = useMemo(() => {
    const cats = ['All', ...new Set(menu.map(item => item.category).filter(Boolean))];
    return cats;
  }, [menu]);

  // Filter menu items
  const filteredMenu = useMemo(() => {
    let items = menu;
    
    if (selectedCategory !== 'All') {
      items = items.filter(item => item.category === selectedCategory);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query)
      );
    }
    
    if (shortCode) {
      items = items.filter(item => 
        item.sku?.toLowerCase().startsWith(shortCode.toLowerCase())
      );
    }
    
    return items;
  }, [menu, selectedCategory, searchQuery, shortCode]);

  useEffect(() => {
    document.title = 'SSPICE POS';
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id || !userData.store_id) {
      navigate('/login');
      return;
    }
    
    const initializePOS = async () => {
      setUser(userData);
      loadMenu(userData.store_id);
      loadOrders(userData.store_id);
      loadStoreConfig(userData.store_id);
      loadHoldOrders();
    };
    initializePOS();

    wsService.connect(`pos:store:${userData.store_id}`);
    wsService.on('order.created', () => loadOrders(userData.store_id));
    wsService.on('order.updated', () => loadOrders(userData.store_id));
    wsService.on('kitchen.ready', () => loadOrders(userData.store_id));
    
    // Listen for day-end event
    wsService.on('day.end', (data) => {
      console.log('[Night] POS: Day ended - refreshing orders');
      toast.info('Day has ended - Orders cleared', { duration: 5000 });
      loadOrders(userData.store_id);
    });

    const interval = setInterval(() => loadOrders(userData.store_id), 10000);
    return () => {
      wsService.disconnect();
      clearInterval(interval);
    };
  }, [navigate]);


  const loadMenu = async (storeId) => {
    // Reads from the same modern catalog Assisted Order uses (catalog_products),
    // not the old menu_items collection - so both screens always show the
    // exact same, current, accurate menu. One tile per product (not per
    // variant) - a multi-size product like "Simply Cheese" opens a size
    // picker on tap, matching Assisted Order's own customize experience,
    // instead of showing each size as its own separate card.
    try {
      const response = await apiClient.get(`/catalog/${storeId}/menu`, { params: { channel: 'pos_walkin' } });
      const flatItems = [];
      const productsWithCategory = [];
      (response.data.categories || []).forEach(cat => {
        (cat.products || []).forEach(p => productsWithCategory.push({ product: p, categoryName: cat.name }));
      });
      productsWithCategory.forEach(({ product: p, categoryName }) => {
        const variants = p.variants && p.variants.length > 0 ? p.variants : [{ id: 'default', name: 'Regular', price: 0, is_default: true }];
        const multiVariant = variants.length > 1;
        const defaultVariant = variants.find(v => v.is_default) || variants[0];
        flatItems.push({
          id: p.id,
          name: p.name,
          price: (defaultVariant.price || 0) / 100,
          priceFrom: multiVariant,
          category: categoryName,
          active: p.active,
          is_combo: p.is_combo || false,
          combo_items: p.combo_components || [],
          image_url: p.image_url || '',
          // Kept in paise here (not converted like item.price above) since
          // ProductCustomizer's formatINR() expects paise and does its own
          // /100 conversion internally - converting twice showed prices
          // like ₹210 as ₹2.
          variants: variants.map(v => ({ id: v.id, name: v.name || 'Regular', price: v.price || 0, is_default: v.is_default })),
          multiVariant
        });
      });
      setMenu(flatItems);
    } catch (error) {
      console.error('Error loading catalog menu:', error);
      try {

        const response = await getMenu(storeId);

        setMenu(response.data);

      } catch (e) {

        console.error('Fallback menu load failed:', e);

      }

    }

  };

  const loadOrders = async (storeId) => {
    try {
      const response = await getOrders(storeId, null, 100);
      const orders = response.data;
      
      // Active orders — also keeps Post Pay orders here after being marked
      // Served/Picked Up/Delivered if payment hasn't been collected yet,
      // so Collect Payment stays reachable instead of the order silently
      // dropping into the Completed list unpaid.
      const active = orders.filter(o =>
        ['NEW', 'PREPARING', 'READY_DINEIN', 'READY_TAKEAWAY', 'READY_DELIVERY', 'RIDER_ASSIGNED', 'OUT_FOR_DELIVERY'].includes(o.status) ||
        (o.is_post_pay && ['SERVED', 'PICKED_UP', 'DELIVERED'].includes(o.status) && o.payment_status !== 'PAID')
      );
      setActiveOrders(active);
      
      // Completed orders — excludes unpaid Post Pay orders (see above),
      // they only land here once payment is actually collected.
      const completed = orders.filter(o => 
        ['SERVED', 'PICKED_UP', 'DELIVERED', 'CANCELLED'].includes(o.status) &&
        !(o.is_post_pay && o.payment_status !== 'PAID')
      );
      setCompletedOrders(completed);
      
      // Load ALL store riders (fail-safe: never depend on 'online' status for manual assignment)
      try {
        let onlineIds = new Set();
        try {
          const activeResp = await apiClient.get(`/riders/active?store_id=${storeId}`);
          (activeResp.data?.riders || []).forEach(r => onlineIds.add(r.rider_id));
        } catch (e) { /* ignore */ }

        const allRidersResponse = await apiClient.get(`/auth/riders?store_id=${storeId}`);
        const ridersList = (allRidersResponse.data || []).map(r => ({
          rider_id: r.id,
          rider_name: r.name,
          phone: r.phone,
          status: onlineIds.has(r.id) ? 'online' : (r.active ? 'available' : 'offline')
        }));
        setRiders(ridersList);
      } catch (e) {
        setRiders([]);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadStoreConfig = async (storeId) => {
    try {
      const response = await getStore(storeId);
      if (response.data?.config) {
        // Merge API config with defaults (to preserve tax/service charge settings)
        setStoreConfig(prev => ({
          ...prev,
          ...response.data.config,
          tax_settings: {
            ...prev.tax_settings,
            ...response.data.config?.tax_settings
          },
          service_charge: {
            ...prev.service_charge,
            ...response.data.config?.service_charge
          }
        }));
      }
      // Load available discounts from localStorage (saved from POSDashboard)
      loadAvailableDiscounts();
      // Load combo offers from API
      loadComboOffers(storeId);
    } catch (error) {
      console.error('Error loading store config:', error);
    }
  };

  const loadAvailableDiscounts = async () => {
    try {
      const res = await apiClient.get('/discounts', { params: { store_id: user.store_id } });
      const normalized = (res.data.discounts || [])
        .filter(d => d.enabled)
        .map(d => ({
          ...d,
          allowedModes: d.allowed_modes || [],
          categoryDiscounts: d.category_discounts || {},
          itemDiscounts: d.item_discounts || {},
          billDiscount: d.bill_discount || 0,
        }));
      setAvailableDiscounts(normalized);
    } catch (error) {
      console.error('Error loading discounts:', error);
      setAvailableDiscounts([]);
    }
  };

  // Load combo offers from API
  const loadComboOffers = async (storeId) => {
    try {
      const response = await apiClient.get(`/offers?store_id=${storeId}`);
      // Filter only enabled COMBO type offers
      const combos = (response.data || []).filter(o => o.type === 'COMBO' && o.enabled);
      setComboOffers(combos);
    } catch (error) {
      console.error('Error loading combo offers:', error);
    }
  };

  // Check if a combo can be applied to the current cart
  const checkComboApplicable = (combo) => {
    if (!combo.combo_items || combo.combo_items.length === 0) return false;
    
    // Check if all combo items are in the cart with required quantities
    for (const comboItem of combo.combo_items) {
      const cartItem = cart.find(c => c.id === comboItem.item_id);
      if (!cartItem || cartItem.qty < comboItem.quantity) {
        return false;
      }
    }
    return true;
  };

  // Get applicable combos for current cart
  const getApplicableCombos = () => {
    return comboOffers.filter(combo => checkComboApplicable(combo));
  };

  // Calculate combo savings
  const calculateComboSavings = (combo) => {
    const individualTotal = combo.combo_items.reduce((sum, ci) => {
      const menuItem = menu.find(m => m.id === ci.item_id);
      return sum + (menuItem?.price || ci.price || 0) * ci.quantity;
    }, 0);
    return individualTotal - (combo.combo_price || 0);
  };

  // Apply combo to cart
  const applyCombo = (combo) => {
    setAppliedCombo(combo);
    setShowComboSelector(false);
    toast.success(` ${combo.name} applied! You save Rs${calculateComboSavings(combo).toFixed(2)}`);
  };

  // Remove applied combo
  const removeCombo = () => {
    setAppliedCombo(null);
    toast.info('Combo removed');
  };

  const applyDiscount = (discount) => {
    setAppliedDiscount(discount);
    setShowDiscountSelector(false);
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
  };

  const loadHoldOrders = () => {
    const saved = localStorage.getItem('holdOrders');
    if (saved) {
      setHoldOrders(JSON.parse(saved));
    }
  };

  const saveHoldOrders = (orders) => {
    localStorage.setItem('holdOrders', JSON.stringify(orders));
    setHoldOrders(orders);
  };

  const handleCustomerPhoneChange = async (phone) => {
    setCustomerInfo({ ...customerInfo, phone });

    if (phone.length !== 10) {
      setCustomerLookupStatus(null);
      return;
    }

    setCustomerLookupLoading(true);
    setCustomerLookupStatus(null);
    try {
      const response = await lookupCustomer(user.store_id, phone);
      if (response.data.found) {
        const customer = response.data.customer;
        const savedAddr = (customer.addresses || []).find((a) => a.is_default) || (customer.addresses || [])[0];
        setCustomerInfo({
          ...customerInfo,
          name: customer.name || '',
          phone: customer.phone,
          address: savedAddr ? savedAddr.address_line : (customer.address || '')
        });
        setCustomerLookupStatus('found');
      } else {
        setCustomerLookupStatus('new');
      }
    } catch (error) {
      console.error('Customer lookup error:', error);
      setCustomerLookupStatus(null);
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const addToCart = (item) => {
    const existing = cart.find(c => c.id === item.id);
    if (existing) {
      setCart(cart.map(c => c.id === item.id ? { ...c, qty: c.qty + 1 } : c));
    } else {
      // Include is_combo and combo_items data for combo items
      setCart([...cart, { 
        ...item, 
        qty: 1, 
        checked: true,
        is_combo: item.is_combo || false,
        combo_items: item.combo_items || []
      }]);
    }
    
    // Show different message for combo items
    if (item.is_combo && item.combo_items && item.combo_items.length > 0) {
      toast.success(` ${item.name} combo added (${item.combo_items.length} items)`);
    } else {
      toast.success(`${item.name} added`);
    }
  };

  // Called when a multi-size item's picker is confirmed - builds one cart
  // line for the specific size chosen, at that size's own price, rather
  // than the item's default/lowest price shown on the tile itself.
  const confirmCustomize = (opts) => {
    const product = customizingItem;
    const variantName = opts.variant?.name || 'Regular';
    const lineId = `${product.id}::${opts.variant?.id || 'default'}`;
    const existing = cart.find(c => c.id === lineId);
    const displayName = product.multiVariant ? `${product.name} (${variantName})` : product.name;
    if (existing) {
      setCart(cart.map(c => c.id === lineId ? { ...c, qty: c.qty + opts.qty } : c));
    } else {
      setCart([...cart, {
        id: lineId,
        name: displayName,
        // opts.variant.price comes back in paise (ProductCustomizer's own
        // unit); the cart itself works in plain rupees like everywhere
        // else in this screen, so convert here specifically.
        price: opts.variant ? opts.variant.price / 100 : product.price,
        category: product.category,
        active: product.active,
        is_combo: product.is_combo || false,
        combo_items: product.combo_items || [],
        image_url: product.image_url || '',
        qty: opts.qty,
        checked: true
      }]);
    }
    toast.success(`${displayName} added`);
    setCustomizingItem(null);
  };

  const updateCartQty = (itemId, delta) => {
    setCart(cart.map(item => {
      if (item.id === itemId) {
        const newQty = item.qty + delta;
        return newQty > 0 ? { ...item, qty: newQty } : null;
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const toggleItemCheck = (itemId) => {
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const toggleItemComp = (itemId) => {
    setCart(cart.map(item => 
      item.id === itemId ? { ...item, isComp: !item.isComp } : item
    ));
  };

  const getCartTotal = () => {
    const checkedItems = cart.filter(item => item.checked);
    // Calculate subtotal excluding complimentary items
    const chargeableItems = checkedItems.filter(item => !item.isComp);
    const compItems = checkedItems.filter(item => item.isComp);
    
    let subtotal = chargeableItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const compTotal = compItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    
    // Calculate combo savings if a combo is applied
    let comboSavings = 0;
    if (appliedCombo && appliedCombo.combo_items && appliedCombo.combo_price) {
      const comboItemsOriginalPrice = appliedCombo.combo_items.reduce((sum, ci) => {
        const menuItem = menu.find(m => m.id === ci.item_id);
        return sum + (menuItem?.price || 0) * ci.quantity;
      }, 0);
      comboSavings = comboItemsOriginalPrice - appliedCombo.combo_price;
    }
    
    // Apply combo savings to subtotal
    subtotal = Math.max(0, subtotal - comboSavings);
    
    // ========== COMPREHENSIVE DISCOUNT ENGINE ==========
    // Priority: Item > Category > Bill
    // Uses selectedDiscount from dashboard configuration
    let discountAmount = 0;
    let discountBreakdown = []; // Track breakdown for display
    
    if (!isComplimentary && selectedDiscount) {
      // Check if current order type is allowed for this discount
      const currentOrderType = orderType.toUpperCase().replace('-', '_').replace(' ', '_');
      const allowedModes = selectedDiscount.allowedModes || [];
      
      // Map order types to mode IDs
      const modeMapping = {
        'DINE_IN': 'DINE_IN',
        'DINEIN': 'DINE_IN',
        'TAKEAWAY': 'TAKEAWAY',
        'PICKUP': 'TAKEAWAY',
        'DELIVERY': 'DELIVERY',
        'SWIGGY': 'SWIGGY',
        'ZOMATO': 'ZOMATO'
      };
      const mappedMode = modeMapping[currentOrderType] || currentOrderType;
      
      if (allowedModes.includes(mappedMode)) {
        // Apply discounts with priority: Item > Category > Bill
        chargeableItems.forEach(item => {
          const itemId = item.id;
          const itemCategory = item.category || 'Uncategorized';
          const itemTotal = item.price * item.qty;
          let itemDiscountPercent = 0;
          let discountSource = '';
          
          // Priority 1: Item-level discount
          if (selectedDiscount.itemDiscounts?.[itemId]?.percent > 0) {
            itemDiscountPercent = selectedDiscount.itemDiscounts[itemId].percent;
            discountSource = 'item';
          }
          // Priority 2: Category-level discount
          else if (selectedDiscount.categoryDiscounts?.[itemCategory] > 0) {
            itemDiscountPercent = selectedDiscount.categoryDiscounts[itemCategory];
            discountSource = 'category';
          }
          // Priority 3: Bill-level fallback discount
          else if (selectedDiscount.billDiscount > 0) {
            itemDiscountPercent = selectedDiscount.billDiscount;
            discountSource = 'bill';
          }
          
          if (itemDiscountPercent > 0) {
            const itemDiscount = (itemTotal * itemDiscountPercent) / 100;
            discountAmount += itemDiscount;
            
            // Track for breakdown display (group by category/source)
            const existingBreakdown = discountBreakdown.find(b => 
              b.source === discountSource && b.category === itemCategory
            );
            if (existingBreakdown) {
              existingBreakdown.subtotal += itemTotal;
              existingBreakdown.discount += itemDiscount;
            } else {
              discountBreakdown.push({
                category: discountSource === 'item' ? item.name : itemCategory,
                percent: itemDiscountPercent,
                subtotal: itemTotal,
                discount: itemDiscount,
                source: discountSource
              });
            }
          }
        });
      }
    }
    
    const afterDiscount = subtotal - discountAmount;
    
    // Service charge - ONLY if taxes are enabled
    const taxSettings = storeConfig.tax_settings || {};
    const taxEnabled = taxSettings.enabled === true;
    
    const serviceChargeSettings = storeConfig.service_charge || {};
    let serviceCharge = 0;
    if (taxEnabled && serviceChargeSettings.enabled && serviceChargeSettings.percent) {
      serviceCharge = (afterDiscount * serviceChargeSettings.percent) / 100;
    }
    
    const afterServiceCharge = afterDiscount + serviceCharge;
    
    // Tax calculation
    let cgst = 0, sgst = 0, tax = 0;
    
    if (taxEnabled) {
      if (taxSettings.cgst && taxSettings.sgst) {
        cgst = (afterServiceCharge * taxSettings.cgst) / 100;
        sgst = (afterServiceCharge * taxSettings.sgst) / 100;
        tax = cgst + sgst;
      } else if (taxSettings.percent) {
        tax = (afterServiceCharge * taxSettings.percent) / 100;
        cgst = tax / 2;
        sgst = tax / 2;
      }
    }
    
    const total = isComplimentary ? 0 : afterServiceCharge + tax;
    
    return { subtotal, compTotal, discountAmount, discountBreakdown, comboSavings, afterDiscount, serviceCharge, cgst, sgst, tax, total, taxEnabled };
  };

  const handleSaveOrder = async (printType = null) => {
    try {
      if (cart.length === 0) {
        toast.error('Cart is empty');
        return;
      }

      if (orderType === 'DELIVERY' && !customerInfo.address) {
        toast.error('Delivery address is required');
        return;
      }

      const checkedItems = cart.filter(item => item.checked);
      if (checkedItems.length === 0) {
        toast.error('No items selected');
        return;
      }

      const { total, discountAmount } = getCartTotal();

      const orderData = {
        store_id: user.store_id,
        order_type: orderType,
        source: 'pos',
        items: checkedItems.map(item => {
          // Cart line ids for multi-size items look like "productId::variantId"
          // (kept that way so two different sizes of the same product show
          // as separate cart lines) - but the backend's real pricing lookup
          // expects the plain product id plus a separate variant id, so
          // split it back apart here specifically for the order payload.
          const [menuItemId, variantId] = item.id.includes('::') ? item.id.split('::') : [item.id, null];
          return {
            menu_item_id: menuItemId,
            menu_item_name: item.name,
            qty: item.qty,
            price: item.price,
            variant: variantId && variantId !== 'default' ? variantId : undefined,
            notes: ''
          };
        }),
        customer: customerInfo.phone ? {
          name: customerInfo.name,
          phone: customerInfo.phone,
          address: customerInfo.address
        } : null,
        discount: isComplimentary ? total : discountAmount,
        discount_info: appliedDiscount ? {
          name: appliedDiscount.name,
          type: appliedDiscount.type,
          value: appliedDiscount.value
        } : null,
        payment_method: paymentMethod,
        payment_status: isPaid ? 'PAID' : 'PENDING',
        special_instructions: specialInstructions,
        is_complimentary: isComplimentary,
        is_post_pay: isPostPay,
        tent_card_number: orderType === 'DINE_IN' ? (customerInfo.table || null) : null
      };

      const order = await createOrder(orderData);
      toast.success(`Order #${order.data.order_number} created!`);
      
      // Handle printing
      if (printType === 'print' || printType === 'kot_print') {
        await handlePrintBill(order.data);
      }
      if (printType === 'kot' || printType === 'kot_print') {
        await handlePrintKOT(order.data);
      }
      if (printType === 'ebill') {
        await handleSendEBill(order.data);
      }

      // Reset state
      resetOrderState();
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order');
    }
  };

  const CART_STORAGE_KEY = 'sspice_pos_in_progress_order';

  const resetOrderState = () => {
    setCart([]);
    setCustomerInfo({ name: '', phone: '', address: '', table: '' });
    setIsPostPay(false);
    setPaymentMethod('CASH');
    setSpecialInstructions('');
    setIsComplimentary(false);
    setIsPaid(false);
    setIsLoyalty(false);
    setSelectedDiscount(null); // Clear selected discount
    setAppliedDiscount(null);
    setCategoryDiscounts({});
    sessionStorage.removeItem(CART_STORAGE_KEY);
  };

  const handleHoldOrder = () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    
    const holdOrder = {
      id: Date.now(),
      cart: [...cart],
      customerInfo: { ...customerInfo },
      orderType,
      paymentMethod,
      specialInstructions,
      createdAt: new Date().toISOString()
    };
    
    const newHoldOrders = [...holdOrders, holdOrder];
    saveHoldOrders(newHoldOrders);
    resetOrderState();
    toast.success('Order held');
  };

  const handleResumeHoldOrder = (holdOrder) => {
    setCart(holdOrder.cart);
    setCustomerInfo(holdOrder.customerInfo);
    setOrderType(holdOrder.orderType);
    setPaymentMethod(holdOrder.paymentMethod);
    setSpecialInstructions(holdOrder.specialInstructions);
    
    const newHoldOrders = holdOrders.filter(o => o.id !== holdOrder.id);
    saveHoldOrders(newHoldOrders);
    setShowHoldModal(false);
    toast.success('Order resumed');
  };

  const handleDeleteHoldOrder = (holdOrderId) => {
    const newHoldOrders = holdOrders.filter(o => o.id !== holdOrderId);
    saveHoldOrders(newHoldOrders);
    toast.success('Hold order deleted');
  };

  const handlePrintKOT = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/kot`);
      const { print_id } = response.data;
      const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/prints/${print_id}/print-page`;
      window.open(printUrl, '_blank');
    } catch (error) {
      console.error('Print KOT error:', error);
      toast.error('Failed to print KOT');
    }
  };

  const handlePrintBill = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/bill`);
      const { print_id } = response.data;
      const printUrl = `${process.env.REACT_APP_BACKEND_URL}/api/prints/${print_id}/print-page`;
      window.open(printUrl, '_blank');
    } catch (error) {
      console.error('Print Bill error:', error);
      toast.error('Failed to print bill');
    }
  };

  // Send E-Bill via WhatsApp/SMS
  const handleSendEBill = async (order) => {
    try {
      const response = await apiClient.post(`/prints/${order.id}/ebill`);
      const { whatsapp_link, bill_url, customer_phone, whatsapp_message } = response.data;
      
      if (whatsapp_link && customer_phone) {
        // Open WhatsApp with pre-filled message
        // A named target (instead of '_blank') lets the browser reuse the
        // same tab across multiple E-Bill sends, rather than piling up a
        // new WhatsApp Web tab every single time.
        window.open(whatsapp_link, 'sspice_whatsapp_ebill');
        toast.success(`E-Bill sent to ${customer_phone} via WhatsApp!`);
      } else if (bill_url) {
        // No customer phone - just copy the link
        navigator.clipboard.writeText(bill_url);
        toast.success('Bill link copied to clipboard! Share with customer.');
        // Also open the bill in new tab
        window.open(bill_url, 'sspice_bill_view');
      } else {
        toast.error('Unable to generate E-Bill');
      }
    } catch (error) {
      console.error('E-Bill error:', error);
      toast.error('Failed to send E-Bill');
    }
  };

  // Open edit customer modal for an order
  const handleOpenEditCustomer = (order) => {
    setSelectedOrderForEdit(order);
    setEditCustomerInfo({
      name: order.customer?.name || '',
      phone: order.customer?.phone || '',
      address: order.customer?.address || ''
    });
    setShowEditCustomerModal(true);
  };

  // Update customer info on existing order
  const handleUpdateOrderCustomer = async () => {
    try {
      if (!selectedOrderForEdit) return;
      
      await apiClient.put(`/orders/${selectedOrderForEdit.id}`, {
        customer: {
          name: editCustomerInfo.name,
          phone: editCustomerInfo.phone,
          address: editCustomerInfo.address
        }
      });
      
      toast.success('Customer details updated!');
      setShowEditCustomerModal(false);
      setSelectedOrderForEdit(null);
      setEditCustomerInfo({ name: '', phone: '', address: '' });
      loadOrders(user.store_id);
    } catch (error) {
      console.error('Update customer error:', error);
      toast.error('Failed to update customer details');
    }
  };

  // Order management functions
  const handleMarkServed = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/status`, { status: 'SERVED' });
      toast.success('Order marked as served');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleMarkPickedUp = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/status`, { status: 'PICKED_UP' });
      toast.success('Order marked as picked up');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const handleAssignRider = async () => {
    try {
      if (!selectedRider || !selectedOrderForRider) {
        toast.error('Please select a rider');
        return;
      }
      const rider = riders.find(r => r.rider_id === selectedRider);
      // Fail-safe endpoint: assigns even if the rider isn't marked "online"
      await apiClient.put(`/orders/${selectedOrderForRider.id}/manual-rider`, {
        rider_id: rider.rider_id,
        rider_name: rider.rider_name
      });
      toast.success(`Rider ${rider.rider_name} assigned`);
      setSelectedRider('');
      const oid = selectedOrderForRider.id;
      await loadOrders(user.store_id);
      // keep modal open with refreshed order so staff can continue managing
      setSelectedOrderForRider((prev) => prev ? { ...prev, rider_name: rider.rider_name, rider_id: rider.rider_id, status: 'RIDER_ASSIGNED' } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign rider');
    }
  };

  const handleRemoveRider = async () => {
    if (!selectedOrderForRider) return;
    try {
      await apiClient.put(`/orders/${selectedOrderForRider.id}/manual-rider`, { rider_id: '', rider_name: '' });
      toast.success('Rider removed');
      setSelectedRider('');
      await loadOrders(user.store_id);
      setSelectedOrderForRider((prev) => prev ? { ...prev, rider_name: null, rider_id: null, status: 'READY_DELIVERY' } : prev);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove rider');
    }
  };

  const handleOverrideStatus = async (newStatus) => {
    if (!selectedOrderForRider) return;
    try {
      await apiClient.put(`/orders/${selectedOrderForRider.id}/override-status`, { status: newStatus });
      toast.success(`Status set to ${newStatus.replace(/_/g, ' ')}`);
      await loadOrders(user.store_id);
      setSelectedOrderForRider((prev) => prev ? { ...prev, status: newStatus } : prev);
      if (['DELIVERED', 'CANCELLED'].includes(newStatus)) {
        setShowAssignRiderModal(false);
        setSelectedOrderForRider(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await apiClient.put(`/orders/${orderId}/cancel`, { cancel_reason: 'Cancelled from POS' });
      toast.success('Order cancelled');
      loadOrders(user.store_id);
    } catch (error) {
      toast.error('Failed to cancel order');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'NEW': 'bg-blue-500',
      'PREPARING': 'bg-yellow-500',
      'READY_DINEIN': 'bg-green-500',
      'READY_TAKEAWAY': 'bg-green-500',
      'READY_DELIVERY': 'bg-purple-500',
      'RIDER_ASSIGNED': 'bg-indigo-500',
      'OUT_FOR_DELIVERY': 'bg-cyan-500',
      'SERVED': 'bg-gray-500',
      'PICKED_UP': 'bg-gray-500',
      'DELIVERED': 'bg-gray-500',
      'CANCELLED': 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getTimeSince = (createdAt) => {
    const diff = Math.floor((new Date() - new Date(createdAt)) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m`;
    const hours = Math.floor(diff / 60);
    return `${hours}h ${diff % 60}m`;
  };

  const { subtotal, compTotal, discountAmount, discountBreakdown, serviceCharge, cgst, sgst, tax, total, taxEnabled } = getCartTotal();

  return (
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      {/* Top Header Bar */}
      <div className="bg-[#C41E3A] text-white shrink-0">
        <div className="flex items-center h-11 px-2 gap-2">
          {/* Logo Section */}
          <div className="flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" className="text-white hover:bg-red-700 h-8 w-8">
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-1">
              <div className="bg-orange-500 rounded-lg p-1">
                <Utensils className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-sm whitespace-nowrap">SSPICE</span>
              <span className="text-[9px] opacity-80 hidden lg:inline whitespace-nowrap">(Designed by Prashant B)</span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center gap-1 shrink-0">
            <Button size="sm" className="bg-orange-500 hover:bg-orange-600 h-7 text-xs px-2" onClick={() => resetOrderState()}>
              New Order
            </Button>
            <Button size="sm" variant="outline" className="text-white border-white/40 hover:bg-red-700 h-7 text-xs px-2" onClick={() => navigate('/pos-dashboard')}>
              Dashboard
            </Button>
          </div>

          {/* Icons - Moved Left with Bigger Icons */}
          <div className="flex items-center gap-0.5 shrink-0">
            {[
              { icon: Eye, label: 'On/Off', onClick: () => navigate('/pos-dashboard') },
              { icon: Eye, label: 'Live', onClick: () => setShowLiveViewModal(true) },
              { icon: Clock, label: 'Orders', onClick: () => setShowOrdersModal(true), badge: activeOrders.length },
              { icon: Clock, label: 'Recent', onClick: () => setShowRecentModal(true) },
              { icon: Pause, label: 'Hold', onClick: () => setShowHoldModal(true), badge: holdOrders.length },
            ].map((item, idx) => (
              <Button 
                key={idx}
                variant="ghost" 
                size="sm" 
                className="text-white hover:bg-red-700 flex flex-col items-center justify-center h-10 w-12 p-0 relative"
                onClick={item.onClick}
              >
                <item.icon className="h-5 w-5" />
                <span className="text-[9px] leading-tight">{item.label}</span>
                {item.badge > 0 && (
                  <Badge className="absolute -top-0.5 -right-0.5 h-4 w-4 p-0 flex items-center justify-center bg-orange-500 text-[8px]">
                    {item.badge}
                  </Badge>
                )}
              </Button>
            ))}
          </div>

          {/* Spacer - Pushes remaining content right */}
          <div className="flex-1" />

          {/* Logout Button - Right side */}
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-red-700 flex flex-col items-center justify-center h-10 px-3"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate('/login');
            }}
          >
            <LogOut className="h-5 w-5" />
            <span className="text-[9px] leading-tight">Logout</span>
          </Button>
        </div>
      </div>

      {/* Search Bar Only */}
      <div className="bg-white border-b flex items-center h-9 px-2 gap-2 shrink-0">
        {/* Search Inputs */}
        <div className="flex items-center gap-1">
          <div className="relative">
            <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400" />
            <Input 
              placeholder="Search" 
              className="pl-6 h-7 w-24 text-xs"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Input 
            placeholder="Code" 
            className="h-7 w-16 text-xs"
            value={shortCode}
            onChange={(e) => setShortCode(e.target.value)}
          />
        </div>
        
        <div className="flex-1" />
      </div>

      {/* Mobile Menu/Cart switcher — phone screens only. On larger screens
          both panels already show side-by-side, so this stays hidden there. */}
      <div className="lg:hidden flex border-b border-gray-200 bg-white sticky top-0 z-20">
        <button
          onClick={() => setMobileView('menu')}
          className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors ${mobileView === 'menu' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-400'}`}
        >
          Menu
        </button>
        <button
          onClick={() => setMobileView('cart')}
          className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors relative ${mobileView === 'cart' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-400'}`}
        >
          Cart
          {cart.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-600 text-white text-[10px] font-bold">
              {cart.length}
            </span>
          )}
        </button>
      </div>

      {/* Main Content Area - PetPooja-style Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT - Categories Sidebar (~15-20% width) */}
        <div className="hidden sm:block w-[120px] lg:w-[140px] bg-[#2D2D2D] shrink-0">
          <ScrollArea className="h-full">
            <div className="py-0.5">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`w-full text-left px-3 py-2 text-[11px] font-medium transition-colors border-l-3 ${
                    selectedCategory === category
                      ? 'bg-green-600 text-white border-l-4 border-green-400'
                      : 'text-gray-300 hover:bg-gray-700 border-l-4 border-transparent'
                  }`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* CENTER - Menu Items Grid (~50% width) */}
        <div className={`flex-1 overflow-hidden bg-gray-100 ${mobileView === 'cart' ? 'hidden lg:block' : 'block'} ${mobileView === 'menu' && cart.length > 0 ? 'pb-14 lg:pb-0' : ''}`}>
          {/* Mobile Category Tabs */}
          <div className="sm:hidden flex overflow-x-auto bg-[#2D2D2D] px-1 py-1 gap-1">
            {categories.map((category) => (
              <button
                key={category}
                className={`shrink-0 px-3 py-1.5 text-[10px] font-medium rounded transition-colors ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>
          
          <ScrollArea className="h-full">
            <div className="p-2">
              {/* 3-column grid like PetPooja */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {filteredMenu.map((item) => (
                  <Card
                    key={item.id}
                    className={`cursor-pointer hover:shadow-lg transition-all border-2 overflow-hidden ${
                      !item.active ? 'grayscale opacity-50 border-gray-300' : 
                      item.is_combo ? 'border-purple-300 hover:border-purple-500' : 
                      'border-gray-200 hover:border-green-500'
                    }`}
                    onClick={() => item.active && (item.multiVariant ? setCustomizingItem(item) : addToCart(item))}
                  >
                    {/* Item Image - full-width, properly covering the card
                        top (matching the ProductCard style used in Assisted
                        Order and the customer app), not a small icon */}
                    <div className={`relative h-20 w-full bg-gray-50 ${!item.active ? 'grayscale' : ''}`}>
                      {storeConfig.show_images_in_pos && item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="h-full w-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        />
                      ) : null}
                      <div className={`h-full w-full items-center justify-center ${item.is_combo ? 'bg-purple-100' : item.active ? 'bg-orange-100' : 'bg-gray-100'} ${storeConfig.show_images_in_pos && item.image_url ? 'hidden' : 'flex'}`}>
                        <Utensils className={`h-6 w-6 ${item.is_combo ? 'text-purple-500' : item.active ? 'text-orange-500' : 'text-gray-400'}`} />
                      </div>
                      <div className="absolute top-1 left-1 right-1 flex justify-between">
                        {item.is_combo ? (
                          <Badge className="h-4 text-[8px] px-1.5 bg-purple-500">
                             COMBO
                          </Badge>
                        ) : (
                          <span></span>
                        )}
                        <Badge 
                          variant={item.active ? "default" : "secondary"} 
                          className={`h-4 text-[8px] px-1.5 ${item.active ? 'bg-green-500' : 'bg-gray-400'}`}
                        >
                          {item.active ? 'ON' : 'OFF'}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-2 text-center">
                      

                      {/* Item Name */}
                      <p className={`text-[10px] font-medium line-clamp-2 min-h-[2rem] leading-tight ${!item.active ? 'text-gray-400' : 'text-gray-800'}`}>
                        {item.name}
                      </p>
                      
                      {/* Combo Items Count */}
                      {item.is_combo && item.combo_items && item.combo_items.length > 0 && (
                        <p className="text-[8px] text-purple-500 mt-0.5">
                          {item.combo_items.length} items included
                        </p>
                      )}
                      
                      {/* Price */}
                      <p className={`text-xs font-bold mt-1 ${item.is_combo ? 'text-purple-600' : item.active ? 'text-green-600' : 'text-gray-400'}`}>
                        {item.priceFrom && <span className="text-[9px] font-normal">from </span>}
                        {formatCurrency(item.price)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>

        {/* RIGHT - Cart/Billing Panel (~35% width) - PetPooja Style */}
        <div className={`w-full lg:w-[380px] bg-white border-l flex-col shrink-0 ${mobileView === 'cart' ? 'flex' : 'hidden lg:flex'}`}>
          {/* Order Type Tabs - TOP OF CART */}
          <div className="flex border-b">
            {[
              { type: 'DINE_IN', label: 'Dine In', color: 'gray' },
              { type: 'DELIVERY', label: 'Delivery', color: 'red' },
              { type: 'TAKEAWAY', label: 'Pick Up', color: 'green' },
            ].map((tab) => (
              <button
                key={tab.type}
                className={`flex-1 py-2 font-semibold text-xs transition-colors ${
                  orderType === tab.type 
                    ? tab.color === 'green' ? 'bg-green-600 text-white' 
                      : tab.color === 'red' ? 'bg-red-600 text-white'
                      : 'bg-gray-700 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                onClick={() => setOrderType(tab.type)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Customer Row — phone/name entry with existing-customer
              detection shown inline (not a disappearing toast), same
              pattern as Assisted Order, so staff see it without opening
              the Customer modal */}
          <div className="px-2 py-1.5 border-b bg-gray-50 space-y-1">
            <div className="flex items-center gap-1.5">
              <Input
                type="tel"
                placeholder="Phone"
                value={customerInfo.phone}
                onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                maxLength={10}
                className="h-7 text-[11px] flex-1"
                data-testid="cart-customer-phone"
              />
              <Input
                placeholder="Name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                className="h-7 text-[11px] flex-1"
                data-testid="cart-customer-name"
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full bg-gray-200 shrink-0"
                onClick={() => setShowCustomerModal(true)}
              >
                <User className="h-3.5 w-3.5" />
              </Button>
            </div>
            {customerLookupLoading && (
              <p className="text-[10px] text-gray-400" data-testid="cart-lookup-searching">Checking…</p>
            )}
            {!customerLookupLoading && customerLookupStatus === 'found' && (
              <div>
                <p className="text-[10px] text-green-600 font-medium" data-testid="cart-lookup-found">
                  ✓ Existing customer{customerInfo.name ? `: ${customerInfo.name}` : ''}
                </p>
                {customerInfo.address && (
                  <p className="text-[10px] text-gray-500" data-testid="cart-lookup-address">📍 {customerInfo.address}</p>
                )}
              </div>
            )}
            {!customerLookupLoading && customerLookupStatus === 'new' && (
              <p className="text-[10px] text-gray-400" data-testid="cart-lookup-new">New customer</p>
            )}
            {customerInfo.table && (
              <Badge variant="outline" className="text-[9px] h-5">T:{customerInfo.table}</Badge>
            )}
          </div>

          {/* Cart Header - Like PetPooja */}
          <div className="flex items-center px-2 py-1.5 bg-gray-100 border-b text-[9px] font-bold text-gray-600 uppercase">
            <span className="flex-1 min-w-0">Items</span>
            <span className="w-16 shrink-0 text-center">Check</span>
            <span className="w-14 shrink-0 text-center">Qty.</span>
            <span className="w-14 shrink-0 text-right">Price</span>
          </div>

          {/* Cart Items Area */}
          <ScrollArea className="flex-1 min-h-[100px]">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <Utensils className="h-8 w-8 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-500">No Item Selected</p>
                <p className="text-[10px] text-gray-400 text-center px-4">Please Select Item from Left Menu Item</p>
              </div>
            ) : (
              <div className="divide-y">
                {cart.map((item) => (
                  <div 
                    key={item.id} 
                    className={`px-2 py-1.5 ${item.isComp ? 'bg-pink-50' : 'hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center">
                      {/* Delete & Item Name */}
                      <div className="flex-1 flex items-center gap-1 min-w-0">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-5 w-5 text-red-500 hover:text-red-700 p-0 shrink-0"
                          onClick={() => removeFromCart(item.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        <div className="min-w-0">
                          <p className={`text-[10px] font-medium truncate ${item.isComp ? 'text-pink-600 line-through' : ''}`} title={item.name}>
                            {item.is_combo && <span className="text-purple-600"> </span>}
                            {item.name}
                          </p>
                        </div>
                      </div>
                      
                      {/* Check & Comp */}
                      <div className="w-16 shrink-0 flex items-center justify-center gap-1">
                        <Checkbox 
                          checked={item.checked} 
                          onCheckedChange={() => toggleItemCheck(item.id)}
                          className="h-4 w-4"
                        />
                        <Button 
                          variant={item.isComp ? "default" : "ghost"} 
                          size="icon" 
                          className={`h-5 w-5 p-0 ${item.isComp ? 'bg-pink-500 text-white' : 'text-pink-400 hover:bg-pink-50'}`}
                          onClick={() => toggleItemComp(item.id)}
                        >
                          <span className="text-[9px]"></span>
                        </Button>
                      </div>
                      
                      {/* Quantity */}
                      <div className="w-14 shrink-0 flex items-center justify-center">
                        <Button variant="outline" size="icon" className="h-5 w-5 p-0" onClick={() => updateCartQty(item.id, -1)}>
                          <Minus className="h-2.5 w-2.5" />
                        </Button>
                        <span className="w-5 text-center font-bold text-[10px]">{item.qty}</span>
                        <Button variant="outline" size="icon" className="h-5 w-5 p-0" onClick={() => updateCartQty(item.id, 1)}>
                          <Plus className="h-2.5 w-2.5" />
                        </Button>
                      </div>
                      
                      {/* Price */}
                      <span className={`w-14 shrink-0 text-right text-[10px] font-bold ${item.isComp ? 'text-pink-500 line-through' : 'text-gray-800'}`}>
                        {formatCurrency(item.price * item.qty)}
                      </span>
                    </div>
                    
                    {/* Show combo included items */}
                    {item.is_combo && item.combo_items && item.combo_items.length > 0 && (
                      <div className="ml-6 mt-1 mb-1 pl-2 border-l-2 border-purple-200">
                        {item.combo_items.map((ci, idx) => (
                          <p key={idx} className="text-[9px] text-purple-600">
                            * {ci.quantity || 1}x {ci.item_name}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Cart Footer - PetPooja Style */}
          <div className="border-t bg-white shrink-0">
            {/* Action Buttons Row */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b">
              <Button size="sm" variant="outline" className="flex-1 h-7 text-[9px] font-medium" onClick={() => setShowSplitModal(true)}>
                Split
              </Button>
              <Button 
                size="sm" 
                variant={selectedDiscount ? "default" : "outline"} 
                className={`flex-1 h-7 text-[9px] font-medium ${selectedDiscount ? 'bg-green-600' : ''}`} 
                onClick={() => setShowDiscountSelector(true)}
              >
                {selectedDiscount 
                  ? `[OK] ${selectedDiscount.name.substring(0, 10)}` 
                  : 'Discount'}
              </Button>
              <div className="flex items-center gap-1 px-2">
                <Checkbox id="complimentary" checked={isComplimentary} onCheckedChange={setIsComplimentary} className="h-4 w-4" />
                <Label htmlFor="complimentary" className="text-[9px]">Comp</Label>
              </div>
            </div>

            {/* Special Notes / Kitchen Instructions */}
            <div className="px-2 py-1.5 border-b">
              <Input
                placeholder="Kitchen notes / Special instructions..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                className="h-7 text-[10px]"
              />
            </div>

            {/* Price Summary */}
            <div className="px-3 py-1.5 space-y-0.5 text-[10px] bg-gray-50 border-b">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              {compTotal > 0 && (
                <div className="flex justify-between text-pink-600">
                  <span>Comp Items:</span>
                  <span>-{formatCurrency(compTotal)}</span>
                </div>
              )}
              {/* Category-wise Discounts Display */}
              {discountBreakdown && discountBreakdown.length > 0 && !isComplimentary && (
                <div className="space-y-1">
                  {discountBreakdown.map((db, idx) => (
                    <div key={idx} className="flex justify-between text-purple-600 text-[10px]">
                      <span>{db.category} ({db.percent}%)</span>
                      <span>-{formatCurrency(db.discount)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-purple-700 font-medium border-t border-purple-200 pt-1">
                    <span className="flex items-center gap-1">
                      Total Discount
                      <button className="text-red-500" onClick={() => setCategoryDiscounts({})}><X className="h-2.5 w-2.5" /></button>
                    </span>
                    <span>-{formatCurrency(discountAmount)}</span>
                  </div>
                </div>
              )}
              {/* Show selected discount name when discount is applied */}
              {selectedDiscount && discountAmount > 0 && !isComplimentary && (!discountBreakdown || discountBreakdown.length === 0) && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    [OK] {selectedDiscount.name}
                    <button className="text-red-500" onClick={() => setSelectedDiscount(null)}><X className="h-2.5 w-2.5" /></button>
                  </span>
                  <span>-{formatCurrency(discountAmount)}</span>
                </div>
              )}
              {/* Service Charge */}
              {serviceCharge > 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Service Charge ({storeConfig.service_charge?.percent || 0}%):</span>
                  <span>{formatCurrency(serviceCharge)}</span>
                </div>
              )}
              {/* Tax Breakdown - CGST + SGST */}
              {tax > 0 && (
                <>
                  <div className="flex justify-between text-gray-600">
                    <span>CGST ({storeConfig.tax_settings?.cgst || 2.5}%):</span>
                    <span>{formatCurrency(cgst)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>SGST ({storeConfig.tax_settings?.sgst || 2.5}%):</span>
                    <span>{formatCurrency(sgst)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Total - Large Display */}
            <div className="flex justify-between items-center px-3 py-2 bg-gray-800 text-white">
              <span className="text-sm font-bold">Total</span>
              {isComplimentary ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs line-through opacity-60">{formatCurrency(subtotal + tax)}</span>
                  <span className="text-xl font-bold">0 </span>
                </div>
              ) : (
                <span className="text-xl font-bold">{formatCurrency(total)}</span>
              )}
            </div>

            {/* Post Pay & Tent Card — moved here from the Customer modal so
                staff can see/set them without leaving the cart view */}
            <div className="flex items-center gap-2 px-2 py-1.5 border-b flex-wrap">
              <button
                type="button"
                data-testid="post-pay-toggle-cart"
                onClick={() => setIsPostPay((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold ${isPostPay ? 'bg-amber-500 text-white border-amber-500' : 'bg-slate-50 text-slate-600 border-slate-200'}`}
              >
                <span className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${isPostPay ? 'border-white bg-white' : 'border-slate-400'}`}>
                  {isPostPay && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}
                </span>
                Post Pay
              </button>
              {orderType === 'DINE_IN' && (
                <Input
                  placeholder="Tent card #"
                  value={customerInfo.table}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, table: e.target.value })}
                  className="h-7 text-xs flex-1 min-w-[90px]"
                  data-testid="tent-card-cart"
                />
              )}
            </div>

            {/* Payment Methods */}
            <div className="flex items-center gap-1 px-2 py-1.5 border-b">
              {['Cash', 'Card', 'Due', 'UPI'].map((method) => (
                <Button
                  key={method}
                  size="sm"
                  disabled={isPostPay}
                  variant={paymentMethod === method.toUpperCase() ? "default" : "outline"}
                  className={`flex-1 h-7 text-[9px] font-medium ${paymentMethod === method.toUpperCase() ? 'bg-green-600' : ''} ${isPostPay ? 'opacity-40 cursor-not-allowed' : ''}`}
                  onClick={() => !isPostPay && setPaymentMethod(method.toUpperCase())}
                >
                  {paymentMethod === method.toUpperCase() && '[OK] '}{method}
                </Button>
              ))}
            </div>
            {isPostPay && (
              <p className="text-[9px] text-amber-600 px-3 py-1 border-b bg-amber-50">
                Post Pay — collect payment later from Live View once the order is ready
              </p>
            )}

            {/* Checkboxes */}
            <div className="flex items-center gap-4 px-3 py-1 border-b">
              <div className="flex items-center gap-1">
                <Checkbox id="isPaid" checked={isPaid} onCheckedChange={setIsPaid} className="h-4 w-4" />
                <Label htmlFor="isPaid" className="text-[10px]">It&apos;s Paid</Label>
              </div>
              <div className="flex items-center gap-1">
                <Checkbox id="isLoyalty" checked={isLoyalty} onCheckedChange={setIsLoyalty} className="h-4 w-4" />
                <Label htmlFor="isLoyalty" className="text-[10px]">Loyalty</Label>
              </div>
            </div>

            {/* Save Buttons - Like PetPooja. EBill deliberately not here -
                it switches away to WhatsApp, which can cause mobile
                browsers to reload the page and lose in-progress cart
                state. Send E-Bill from the order list/Live View instead,
                once the order is already safely booked. */}
            <div className="grid grid-cols-5 gap-0.5 p-1.5">
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-[8px] h-8 px-0" onClick={() => handleSaveOrder()}>{isPostPay ? 'Book Order' : 'Save'}</Button>
              <Button size="sm" className="bg-green-700 hover:bg-green-800 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('print')}>Save & Print</Button>
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('kot')}>KOT</Button>
              <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-[8px] h-8 px-0" onClick={() => handleSaveOrder('kot_print')}>KOT+P</Button>
              <Button size="sm" className="bg-gray-600 hover:bg-gray-700 text-[8px] h-8 px-0" onClick={handleHoldOrder}>Hold</Button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Modal */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-3">
            <div>
              <Label className="text-sm">Phone Number</Label>
              <Input
                type="tel"
                placeholder="10-digit phone"
                value={customerInfo.phone}
                onChange={(e) => handleCustomerPhoneChange(e.target.value)}
                maxLength={10}
              />
              {customerLookupLoading && <p className="text-xs text-gray-500 mt-1">Looking up...</p>}
            </div>
            <div>
              <Label className="text-sm">Name</Label>
              <Input
                placeholder="Customer name"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
              />
            </div>
            {orderType === 'DELIVERY' && (
              <div>
                <Label className="text-sm">Delivery Address *</Label>
                <Textarea
                  placeholder="Full address"
                  value={customerInfo.address}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                  rows={2}
                />
              </div>
            )}
            <div>
              <Label className="text-sm">Special Instructions</Label>
              <Textarea
                placeholder="Kitchen notes..."
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerModal(false)}>Cancel</Button>
            <Button className="bg-green-500" onClick={() => setShowCustomerModal(false)}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hold Orders Modal */}
      <Dialog open={showHoldModal} onOpenChange={setShowHoldModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Hold Orders ({holdOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-80">
            {holdOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No orders on hold</div>
            ) : (
              <div className="space-y-2">
                {holdOrders.map((order) => (
                  <Card key={order.id} className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-medium text-sm">{order.orderType}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(order.createdAt).toLocaleTimeString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold">{formatCurrency(order.cart.reduce((s, i) => s + i.price * i.qty, 0))}</p>
                        <p className="text-xs text-gray-500">{order.cart.length} items</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-green-500 h-8"
                        onClick={() => handleResumeHoldOrder(order)}
                      >
                        Resume
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        className="h-8"
                        onClick={() => handleDeleteHoldOrder(order.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Live View Modal - NEW IMPLEMENTATION */}
      <Dialog open={showLiveViewModal} onOpenChange={setShowLiveViewModal}>
        <DialogContent className="w-[95vw] max-w-6xl max-h-[85vh] p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="flex items-center gap-2">
                Live Order View
                {onlineOnlyFilter && <Badge className="bg-green-500 text-white text-xs">Online Only</Badge>}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={onlineOnlyFilter ? 'default' : 'outline'}
                  className={onlineOnlyFilter ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setOnlineOnlyFilter((v) => !v)}
                >
                  <Globe className="h-4 w-4 mr-1" /> {onlineOnlyFilter ? 'Showing Online Only' : 'Show Online Only'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => loadOrders(user?.store_id)}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="w-20">Order #</TableHead>
                  <TableHead className="w-24">Type</TableHead>
                  <TableHead className="w-28">Status</TableHead>
                  <TableHead className="w-24">Time</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead className="w-24 text-right">Total</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  // Cancelled orders don't belong in a "live" working view —
                  // there's nothing actionable left to do with them here.
                  // Kept fully visible elsewhere (Orders history, reports),
                  // just not cluttering the active-order workflow screen.
                  const nonCancelled = activeOrders.filter((o) => o.status !== 'CANCELLED');
                  const displayedOrders = onlineOnlyFilter
                    ? nonCancelled.filter((o) => o.source === 'customer')
                    : nonCancelled;
                  if (displayedOrders.length === 0) {
                    return (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                          {onlineOnlyFilter ? 'No active online orders' : 'No active orders'}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return displayedOrders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-gray-50">
                      <TableCell className="font-bold">#{order.order_number}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {order.order_type === 'DINE_IN' && '[Dine-In]'}
                          {order.order_type === 'TAKEAWAY' && '[Takeaway]'}
                          {order.order_type === 'DELIVERY' && '[Delivery]'}
                          {' '}{order.order_type}
                        </Badge>
                        {order.source === 'customer' && (
                          <Badge className="bg-green-500 text-white text-[10px] ml-1">
                            <Globe className="h-2.5 w-2.5 mr-0.5" />Online
                          </Badge>
                        )}
                        {order.tent_card_number && (
                          <Badge className="bg-slate-500 text-white text-[10px] ml-1">
                            Tent #{order.tent_card_number}
                          </Badge>
                        )}
                        {order.is_post_pay && order.payment_status !== 'PAID' && (
                          <Badge className="bg-amber-500 text-white text-[10px] ml-1">
                            Payment Pending
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(order.status)} text-white text-xs`}>
                          {order.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">
                        {getTimeSince(order.created_at)}
                      </TableCell>
                      <TableCell>
                        {order.customer ? (
                          <div className="text-xs">
                            <p className="font-medium">{order.customer.name}</p>
                            <p className="text-gray-500">{order.customer.phone}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">Walk-in</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs max-w-[150px] max-h-16 overflow-y-auto space-y-0.5">
                          {order.items?.map((item, idx) => (
                            <div key={idx}>{item.qty}x {item.menu_item_name}</div>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-orange-600">
                        {formatCurrency(order.total)}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {order.order_type === 'DINE_IN' && order.status === 'READY_DINEIN' && (
                            <Button size="sm" className="h-6 text-[10px] bg-green-500" onClick={() => handleMarkServed(order.id)}>
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Served
                            </Button>
                          )}
                          {order.order_type === 'TAKEAWAY' && order.status === 'READY_TAKEAWAY' && (
                            <Button size="sm" className="h-6 text-[10px] bg-green-500" onClick={() => handleMarkPickedUp(order.id)}>
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Picked
                            </Button>
                          )}
                          {order.is_post_pay && order.payment_status !== 'PAID' && (
                            <Button
                              size="sm"
                              className="h-6 text-[10px] bg-amber-500 hover:bg-amber-600"
                              data-testid={`collect-payment-btn-${order.id}`}
                              onClick={() => setCollectPaymentOrder(order)}
                            >
                              Collect Payment
                            </Button>
                          )}
                          {order.is_post_pay &&
                            !['CANCELLED', 'SERVED', 'PICKED_UP', 'DELIVERED'].includes(order.status) &&
                            order.payment_status !== 'PAID' && (
                            <Button
                              size="sm"
                              className="h-6 text-[10px] bg-orange-600 hover:bg-orange-700"
                              data-testid={`postpay-edit-order-btn-${order.id}`}
                              onClick={() => {
                                setPostPayEditOrder(order);
                                setShowPostPayEditModal(true);
                              }}
                            >
                              Edit Items
                            </Button>
                          )}
                          {order.order_type === 'DELIVERY' && ['READY_DELIVERY', 'RIDER_ASSIGNED', 'OUT_FOR_DELIVERY'].includes(order.status) && (
                            <Button 
                              size="sm" 
                              className="h-6 text-[10px] bg-purple-500 hover:bg-purple-600"
                              data-testid={`manage-delivery-btn-${order.id}`}
                              onClick={() => {
                                setSelectedOrderForRider(order);
                                setSelectedRider(order.rider_id || '');
                                setShowAssignRiderModal(true);
                              }}
                            >
                              <UserPlus className="h-3 w-3 mr-0.5" /> Delivery
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handleOpenEditCustomer(order)}
                            title="Edit Customer"
                          >
                            <User className="h-3 w-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-purple-50 border-purple-300 text-purple-600 hover:bg-purple-100"
                            onClick={() => {
                              setSelectedOrderForDetails(order);
                              setShowOrderDetailsModal(true);
                            }}
                            title="View Full Order Details"
                            data-testid="view-order-details-btn"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100"
                            onClick={() => handlePrintBill(order)}
                            title="Print Bill"
                          >
                            Bill
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100"
                            onClick={() => handlePrintKOT(order)}
                            title="Print KOT"
                          >
                            KOT
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px] bg-green-50 border-green-300 text-green-600 hover:bg-green-100"
                            onClick={() => handleSendEBill(order)}
                            title="Send E-Bill via WhatsApp"
                          >
                            E-Bill
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="h-6 text-[10px]"
                            onClick={() => handleCancelOrder(order.id)}
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Modal - Full Item List with Special Instructions */}
      <Dialog open={showOrderDetailsModal} onOpenChange={setShowOrderDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="text-xl">Order #{selectedOrderForDetails?.order_number}</span>
              <Badge className={`${getStatusColor(selectedOrderForDetails?.status || '')} text-white`}>
                {selectedOrderForDetails?.status?.replace(/_/g, ' ')}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedOrderForDetails && (
            <div className="space-y-4">
              {/* Order Info Header */}
              <div className="grid grid-cols-2 gap-4 p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500">Order Type</p>
                  <p className="font-semibold flex items-center gap-1">
                    {selectedOrderForDetails.order_type === 'DINE_IN' && '[Dine-In]'}
                    {selectedOrderForDetails.order_type === 'TAKEAWAY' && '[Takeaway]'}
                    {selectedOrderForDetails.order_type === 'DELIVERY' && '[Delivery]'}
                    {selectedOrderForDetails.order_type}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Time</p>
                  <p className="font-semibold">{getTimeSince(selectedOrderForDetails.created_at)}</p>
                </div>
                {selectedOrderForDetails.customer && (
                  <>
                    <div>
                      <p className="text-xs text-gray-500">Customer</p>
                      <p className="font-semibold">{selectedOrderForDetails.customer.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Phone</p>
                      <p className="font-semibold">{selectedOrderForDetails.customer.phone || 'N/A'}</p>
                    </div>
                    {selectedOrderForDetails.order_type === 'DELIVERY' && selectedOrderForDetails.customer.address && (
                      <div className="col-span-2">
                        <p className="text-xs text-gray-500">Delivery Address</p>
                        <p className="font-semibold">{selectedOrderForDetails.customer.address}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Items List */}
              <div>
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Utensils className="h-4 w-4" />
                  Items Ordered ({selectedOrderForDetails.items?.length || 0})
                </h4>
                <ScrollArea className="max-h-[300px]">
                  <div className="space-y-2">
                    {selectedOrderForDetails.items?.map((item, idx) => (
                      <div 
                        key={idx} 
                        className="p-3 border rounded-lg bg-white hover:bg-gray-50"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-sm font-bold">
                                {item.qty}x
                              </span>
                              <span className="font-semibold">{item.menu_item_name}</span>
                            </div>
                            {(item.variant || (item.modifiers && item.modifiers.length > 0)) && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {item.variant && (
                                  <span className="inline-block bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded text-xs font-medium">
                                    {typeof item.variant === 'string' ? item.variant : item.variant.name}
                                  </span>
                                )}
                                {(item.modifiers || []).map((mod, midx) => (
                                  <span key={midx} className="inline-block bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded text-xs font-medium">
                                    {typeof mod === 'string' ? mod : mod.name}
                                  </span>
                                ))}
                              </div>
                            )}
                            {item.notes && (
                              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                <span className="font-medium text-yellow-700"> Special Instructions: </span>
                                <span className="text-yellow-800">{item.notes}</span>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-orange-600">
                              {formatCurrency(item.price * item.qty)}
                            </p>
                            <p className="text-xs text-gray-500">
                              @ {formatCurrency(item.price)} each
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Order-level Special Instructions */}
              {selectedOrderForDetails.special_instructions && (
                <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <h4 className="font-semibold text-yellow-700 flex items-center gap-2 mb-1">
                     Order Instructions (Sent to Kitchen)
                  </h4>
                  <p className="text-yellow-800">{selectedOrderForDetails.special_instructions}</p>
                </div>
              )}

              {/* Order Totals */}
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedOrderForDetails.subtotal)}</span>
                </div>
                {selectedOrderForDetails.tax > 0 && (
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tax</span>
                    <span>{formatCurrency(selectedOrderForDetails.tax)}</span>
                  </div>
                )}
                {selectedOrderForDetails.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{formatCurrency(selectedOrderForDetails.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span className="text-orange-600">{formatCurrency(selectedOrderForDetails.total)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    handlePrintKOT(selectedOrderForDetails);
                  }}
                >
                   Reprint KOT
                </Button>
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    handlePrintBill(selectedOrderForDetails);
                  }}
                >
                   Print Bill
                </Button>
                <Button 
                  onClick={() => setShowOrderDetailsModal(false)}
                  className="flex-1 bg-gray-600 hover:bg-gray-700"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Active Orders Modal */}
      <Dialog open={showOrdersModal} onOpenChange={setShowOrdersModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Active Orders ({activeOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {activeOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No active orders</div>
            ) : (
              <div className="space-y-2">
                {activeOrders.map((order) => (
                  <Card key={order.id} className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold">#{order.order_number}</p>
                        <Badge className={`${getStatusColor(order.status)} text-white mt-1`}>{order.status}</Badge>
                        <p className="text-xs text-gray-500 mt-1">{order.order_type}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">{formatCurrency(order.total)}</p>
                        <p className="text-xs text-gray-500">{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {order.customer && (
                      <div className="mt-2 text-sm text-gray-600">
                        <p>{order.customer.name} - {order.customer.phone}</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Recent Orders Modal */}
      <Dialog open={showRecentModal} onOpenChange={setShowRecentModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Recent Completed Orders ({completedOrders.length})</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh]">
            {completedOrders.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No completed orders</div>
            ) : (
              <div className="space-y-2">
                {completedOrders.slice(0, 20).map((order) => (
                  <Card key={order.id} className={`p-3 ${order.status === 'CANCELLED' ? 'border-red-300' : ''}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className={`font-bold ${order.status === 'CANCELLED' ? 'text-red-600' : ''}`}>
                          #{order.order_number}
                        </p>
                        <Badge className={`${getStatusColor(order.status)} text-white mt-1`}>{order.status}</Badge>
                        <p className="text-xs text-gray-500 mt-1">{order.order_type}</p>
                        {order.customer && (
                          <p className="text-xs text-gray-600 mt-1">
                            {order.customer.name} - {order.customer.phone}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${order.status === 'CANCELLED' ? 'text-red-600' : ''}`}>
                          {formatCurrency(order.total)}
                        </p>
                        <p className="text-xs text-gray-500">{getTimeSince(order.created_at)} ago</p>
                        <div className="flex gap-1 mt-2 justify-end">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handleOpenEditCustomer(order)}
                          >
                            <User className="h-3 w-3 mr-1" /> Edit Customer
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-6 text-[10px]"
                            onClick={() => handlePrintBill(order)}
                          >
                            <Printer className="h-3 w-3 mr-1" /> Print
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Manage Delivery Modal - Fail-Safe (assign / reassign / remove rider + manual status) */}
      <Dialog open={showAssignRiderModal} onOpenChange={setShowAssignRiderModal}>
        <DialogContent className="max-w-md" data-testid="manage-delivery-modal">
          <DialogHeader>
            <DialogTitle>Manage Delivery - Order #{selectedOrderForRider?.order_number}</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {/* Current state */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
              <span>Status: <b>{(selectedOrderForRider?.status || '').replace(/_/g, ' ')}</b></span>
              <span className="text-gray-600">
                Rider: <b>{selectedOrderForRider?.rider_name || 'None'}</b>
              </span>
            </div>

            {/* Rider assignment */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Assign / Reassign Rider</Label>
                <Button size="sm" variant="outline" onClick={() => loadOrders(user?.store_id)}>Refresh</Button>
              </div>
              <Select value={selectedRider} onValueChange={setSelectedRider}>
                <SelectTrigger data-testid="delivery-rider-select">
                  <SelectValue placeholder="Choose a rider" />
                </SelectTrigger>
                <SelectContent>
                  {riders && riders.length > 0 ? (
                    riders.map((rider) => (
                      <SelectItem key={rider.rider_id} value={rider.rider_id}>
                        {rider.rider_name} ({rider.phone}) {rider.status === 'online' ? '[Online]' : ''}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="none" disabled>No riders found</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="assign-rider-confirm-btn" onClick={handleAssignRider} disabled={!selectedRider}>
                  {selectedOrderForRider?.rider_id ? 'Reassign' : 'Assign'} Rider
                </Button>
                {selectedOrderForRider?.rider_id && (
                  <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" data-testid="remove-rider-btn" onClick={handleRemoveRider}>
                    Remove
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">{riders?.length || 0} rider(s) available. Assignment works even if a rider hasn't marked themselves online.</p>
            </div>

            {/* Manual status fail-safe */}
            <div className="space-y-2 border-t pt-3">
              <Label className="text-orange-600">Manual Status Override (fail-safe)</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button size="sm" variant="outline" data-testid="override-ready-delivery" onClick={() => handleOverrideStatus('READY_DELIVERY')}>Ready for Delivery</Button>
                <Button size="sm" variant="outline" data-testid="override-rider-assigned" onClick={() => handleOverrideStatus('RIDER_ASSIGNED')}>Rider Assigned</Button>
                <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600" data-testid="override-out-for-delivery" onClick={() => handleOverrideStatus('OUT_FOR_DELIVERY')}>Out for Delivery</Button>
                <Button size="sm" className="bg-green-600 hover:bg-green-700" data-testid="override-delivered" onClick={() => handleOverrideStatus('DELIVERED')}>Mark Delivered</Button>
              </div>
              <p className="text-xs text-gray-500">Use these to progress an order manually if it ever gets stuck.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAssignRiderModal(false); setSelectedOrderForRider(null); setSelectedRider(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Bill Modal */}
      <Dialog open={showSplitModal} onOpenChange={setShowSplitModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Split Bill</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-gray-500">
            Split bill feature coming soon
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSplitModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance Order Modal */}
      <Dialog open={showAdvanceModal} onOpenChange={setShowAdvanceModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Advance Order</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-center text-gray-500">
            Advance order scheduling coming soon
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAdvanceModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Customer Modal */}
      <Dialog open={showEditCustomerModal} onOpenChange={setShowEditCustomerModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit Customer - Order #{selectedOrderForEdit?.order_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Customer Name</Label>
              <Input
                placeholder="Enter customer name"
                value={editCustomerInfo.name}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, name: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Phone Number</Label>
              <Input
                type="tel"
                placeholder="10-digit mobile number"
                value={editCustomerInfo.phone}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, phone: e.target.value })}
                maxLength={10}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Delivery Address</Label>
              <Textarea
                placeholder="Enter full delivery address"
                value={editCustomerInfo.address}
                onChange={(e) => setEditCustomerInfo({ ...editCustomerInfo, address: e.target.value })}
                rows={3}
                className="mt-1"
              />
            </div>
            {selectedOrderForEdit && (
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <p><strong>Order Type:</strong> {selectedOrderForEdit.order_type}</p>
                <p><strong>Total:</strong> {formatCurrency(selectedOrderForEdit.total)}</p>
                <p><strong>Status:</strong> {selectedOrderForEdit.status}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditCustomerModal(false);
              setSelectedOrderForEdit(null);
            }}>
              Cancel
            </Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleUpdateOrderCustomer}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIMPLIFIED Discount Selector Modal - APPLY ONLY, NO CONFIGURATION */}
      <Dialog open={showDiscountSelector} onOpenChange={setShowDiscountSelector}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Apply Discount
            </DialogTitle>
            <p className="text-xs text-gray-500">
              Select a pre-configured discount. Order mode: <Badge variant="outline" className="ml-1">{orderType}</Badge>
            </p>
          </DialogHeader>
          <ScrollArea className="max-h-[55vh]">
            <div className="space-y-3 py-4">
              {/* Filter discounts by current order mode */}
              {(() => {
                const currentOrderType = orderType.toUpperCase().replace('-', '_').replace(' ', '_');
                const modeMapping = {
                  'DINE_IN': 'DINE_IN', 'DINEIN': 'DINE_IN',
                  'TAKEAWAY': 'TAKEAWAY', 'PICKUP': 'TAKEAWAY',
                  'DELIVERY': 'DELIVERY', 'SWIGGY': 'SWIGGY', 'ZOMATO': 'ZOMATO'
                };
                const mappedMode = modeMapping[currentOrderType] || currentOrderType;
                
                // Get discounts from localStorage
                const savedDiscounts = JSON.parse(localStorage.getItem('pos_discounts_v2') || '{"discounts":[]}');
                const allDiscounts = savedDiscounts.discounts || [];
                
                // Filter by mode and enabled status
                const applicableDiscounts = allDiscounts.filter(d => 
                  d.enabled && d.allowedModes?.includes(mappedMode)
                );
                const blockedDiscounts = allDiscounts.filter(d => 
                  d.enabled && !d.allowedModes?.includes(mappedMode)
                );
                
                return (
                  <>
                    {applicableDiscounts.length === 0 && blockedDiscounts.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <Percent className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        <p>No discounts configured</p>
                        <p className="text-xs mt-2">Go to Dashboard -> Discount to create discounts</p>
                      </div>
                    )}
                    
                    {applicableDiscounts.length > 0 && (
                      <>
                        <p className="text-xs font-medium text-green-600">[OK] Available for {orderType}:</p>
                        <div className="space-y-2">
                          {applicableDiscounts.map((discount) => {
                            const isSelected = selectedDiscount?.id === discount.id;
                            return (
                              <div
                                key={discount.id}
                                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                                  isSelected 
                                    ? 'bg-green-50 border-green-500 ring-2 ring-green-300' 
                                    : 'bg-white hover:bg-gray-50 border-gray-200'
                                }`}
                                onClick={() => {
                                  setSelectedDiscount(isSelected ? null : discount);
                                  setShowDiscountSelector(false);
                                  if (!isSelected) {
                                    toast.success(`Applied: ${discount.name}`);
                                  } else {
                                    toast.info('Discount removed');
                                  }
                                }}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-bold">{discount.name}</span>
                                  {isSelected && <Badge className="bg-green-500">Applied</Badge>}
                                </div>
                                
                                {/* Show discount summary */}
                                <div className="text-xs space-y-1 text-gray-600">
                                  {discount.categoryDiscounts && Object.keys(discount.categoryDiscounts).length > 0 && (
                                    <p> Categories: {Object.entries(discount.categoryDiscounts).map(([c,p]) => `${c} ${p}%`).join(', ')}</p>
                                  )}
                                  {discount.itemDiscounts && Object.keys(discount.itemDiscounts).length > 0 && (
                                    <p> Items: {Object.values(discount.itemDiscounts).map(i => `${i.name} ${i.percent}%`).slice(0,3).join(', ')}{Object.keys(discount.itemDiscounts).length > 3 ? '...' : ''}</p>
                                  )}
                                  {discount.billDiscount > 0 && (
                                    <p> Bill: {discount.billDiscount}% (fallback)</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    )}
                    
                    {blockedDiscounts.length > 0 && (
                      <>
                        <p className="text-xs font-medium text-red-500 mt-4">[X] Not available for {orderType}:</p>
                        <div className="space-y-2 opacity-50">
                          {blockedDiscounts.map((discount) => (
                            <div
                              key={discount.id}
                              className="p-3 rounded-lg border border-red-200 bg-red-50 cursor-not-allowed"
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium line-through">{discount.name}</span>
                                <Badge variant="outline" className="text-red-500 border-red-300">Blocked</Badge>
                              </div>
                              <p className="text-[10px] text-red-400 mt-1">
                                Only for: {discount.allowedModes?.join(', ')}
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              
              {/* Current Selection Info */}
              {selectedDiscount && (
                <div className="bg-green-50 p-3 rounded-lg border border-green-200 mt-4">
                  <p className="text-xs font-medium text-green-700 mb-1">[OK] Active Discount:</p>
                  <p className="text-sm font-bold">{selectedDiscount.name}</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full mt-2 text-red-500 border-red-200"
                    onClick={() => { setSelectedDiscount(null); setShowDiscountSelector(false); toast.info('Discount removed'); }}
                  >
                    <X className="h-3 w-3 mr-1" /> Remove Discount
                  </Button>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiscountSelector(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collect Payment for Post Pay orders - reuses the same dialog used
          at order creation, just opened later for an existing order.
          IMPORTANT: collectPaymentOrder is only a snapshot captured at the
          moment the button was clicked. The orders list keeps refreshing
          live in the background (10s poll + websocket updates), so if an
          item gets added/removed on this same order while the popup is
          still open, that snapshot goes stale — the popup would keep
          showing the old item list and the old total, letting staff
          collect the wrong amount. We re-resolve to the live order from
          `orders` on every render instead, falling back to the snapshot
          only if the order has since dropped out of the list entirely
          (e.g. just got marked paid/completed elsewhere). */}
      <PaymentDialog
        order={
          collectPaymentOrder
            ? [...activeOrders, ...completedOrders].find((o) => o.id === collectPaymentOrder.id) || collectPaymentOrder
            : null
        }
        open={!!collectPaymentOrder}
        onClose={() => setCollectPaymentOrder(null)}
        onPaid={() => { setCollectPaymentOrder(null); loadOrders(user?.store_id); }}
      />

      {/* Post Pay item-level edit — add/remove items after the kitchen has
          already started, for genuine Post Pay orders only. */}
      <PostPayEditModal
        order={postPayEditOrder}
        open={showPostPayEditModal}
        onClose={() => setShowPostPayEditModal(false)}
        onSuccess={() => { setShowPostPayEditModal(false); loadOrders(user?.store_id); }}
      />

      {/* Size picker for multi-variant items - the same customize
          experience Assisted Order uses, reused directly here. */}
      {customizingItem && (
        <ProductCustomizer
          product={customizingItem}
          onClose={() => setCustomizingItem(null)}
          onConfirm={confirmCustomize}
        />
      )}

      {/* Persistent mini-cart bar - phone screens only, while browsing the
          menu. Keeps the running total and item count always visible
          without needing to switch away from the menu (which was creating
          real risk of duplicate/missed items when constantly toggling
          back and forth). Tapping it opens the full cart. */}
      {mobileView === 'menu' && cart.length > 0 && (
        <button
          onClick={() => setMobileView('cart')}
          className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-green-600 text-white px-4 py-3 flex items-center justify-between shadow-lg"
        >
          <span className="flex items-center gap-2 font-semibold text-sm">
            <span className="bg-white text-green-600 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
              {cart.length}
            </span>
            View Cart
          </span>
          <span className="font-bold">{formatCurrency(total)}</span>
        </button>
      )}
    </div>
  );
};

export default POSAppNew;